import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Building2, Edit, MapPin, Phone, Mail, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import api, { employeeAPI } from '../services/api';

const EMPTY_FORM = { name: '', code: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', gstin: '', manager_id: '', manager_name: '', is_head_office: false };

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/branches').then(r => setBranches(r.data?.branches || []))
      .catch(() => toast.error('Failed to load branches'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); employeeAPI.list().then(r => setEmployees(r.data || [])).catch(() => {}); }, [load]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Branch name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/branches/${editing}`, form);
        toast.success('Branch updated');
      } else {
        await api.post('/branches', form);
        toast.success('Branch created');
      }
      setShowForm(false); setEditing(null);
      setForm({ ...EMPTY_FORM });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const openEdit = (br) => {
    setForm({ name: br.name, code: br.code, address: br.address, city: br.city, state: br.state, pincode: br.pincode, phone: br.phone, email: br.email, gstin: br.gstin, manager_id: br.manager_id || '', manager_name: br.manager_name || '', is_head_office: br.is_head_office });
    setEditing(br.branch_id);
    setShowForm(true);
  };

  const handleDeactivate = async (id) => {
    try { await api.delete(`/branches/${id}`); toast.success('Branch deactivated'); load(); }
    catch { toast.error('Failed to deactivate'); }
  };

  const activeBranches = branches.filter(b => b.is_active);

  return (
    <div data-testid="branches-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))]">Branches</h1>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[hsl(0,0%,55%)] mt-1">Manage company branches and locations</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ ...EMPTY_FORM }); setShowForm(true); }}
          className="btn-accent flex items-center gap-2" data-testid="add-branch-btn">
          <Plus className="w-4 h-4" /> Add Branch
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-5 h-5 border-2 border-[hsl(0,0%,3.9%)] dark:border-white border-t-transparent rounded-full animate-spin" /></div>
      ) : activeBranches.length === 0 ? (
        <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md flex flex-col items-center justify-center py-20">
          <Building2 className="w-12 h-12 text-[hsl(0,0%,80%)] dark:text-[hsl(0,0%,25%)] mb-4" strokeWidth={1} />
          <p className="text-[hsl(0,0%,55%)] text-sm mb-1">No branches yet</p>
          <p className="text-[10px] text-[hsl(0,0%,65%)]">Create your first branch to enable multi-branch tracking</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeBranches.map(br => (
            <div key={br.branch_id} className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-5 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm group" data-testid={`branch-card-${br.branch_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-[hsl(0,0%,96%)] dark:bg-[hsl(0,0%,12%)] flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-[hsl(0,0%,40%)]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-[hsl(var(--foreground))]">{br.name}</h3>
                    <span className="text-[9px] font-mono text-[hsl(0,0%,55%)] bg-[hsl(0,0%,96%)] dark:bg-[hsl(0,0%,12%)] px-1.5 py-0.5 rounded">{br.code}</span>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button onClick={() => openEdit(br)} className="p-1.5 rounded-md hover:bg-[hsl(0,0%,96%)] dark:hover:bg-[hsl(0,0%,12%)] transition" data-testid={`edit-branch-${br.branch_id}`}>
                    <Edit className="w-3.5 h-3.5 text-[hsl(0,0%,45%)]" />
                  </button>
                  <button onClick={() => handleDeactivate(br.branch_id)} className="p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/30 transition" data-testid={`deactivate-branch-${br.branch_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--destructive))]" />
                  </button>
                </div>
              </div>
              {br.is_head_office && (
                <span className="inline-flex items-center px-2 py-0.5 text-[9px] font-bold rounded border bg-[hsl(0,0%,96%)] text-[hsl(0,0%,30%)] border-[hsl(0,0%,85%)] dark:bg-[hsl(0,0%,12%)] dark:text-[hsl(0,0%,70%)] dark:border-[hsl(0,0%,20%)] uppercase tracking-[0.1em] mb-2">Head Office</span>
              )}
              <div className="space-y-1.5 text-[12px] text-[hsl(0,0%,45%)]">
                {br.manager_name && <div className="flex items-center gap-2 font-medium text-[hsl(0,0%,35%)]"><span className="text-[10px] uppercase tracking-wide text-[hsl(0,0%,60%)]">Manager:</span>{br.manager_name}</div>}
                {br.address && <div className="flex items-start gap-2"><MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[hsl(0,0%,60%)]" />{br.address}{br.city ? `, ${br.city}` : ''}{br.state ? `, ${br.state}` : ''}{br.pincode ? ` - ${br.pincode}` : ''}</div>}
                {br.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-[hsl(0,0%,60%)]" />{br.phone}</div>}
                {br.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-[hsl(0,0%,60%)]" />{br.email}</div>}
                {br.gstin && <div className="font-mono text-[10px] text-[hsl(0,0%,55%)] mt-1">GSTIN: {br.gstin}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold tracking-tight">{editing ? 'Edit Branch' : 'New Branch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Branch Name *</label><input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="branch-name" /></div>
              <div><label className="form-label">Code</label><input className="form-input" value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder="e.g. MUM" data-testid="branch-code" /></div>
            </div>
            <div><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="form-label">City</label><input className="form-input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
              <div><label className="form-label">State</label><input className="form-input" value={form.state} onChange={e => setForm({...form, state: e.target.value})} /></div>
              <div><label className="form-label">Pincode</label><input className="form-input" value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div><label className="form-label">Email</label><input className="form-input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            </div>
            <div><label className="form-label">GSTIN</label><input className="form-input font-mono" value={form.gstin} onChange={e => setForm({...form, gstin: e.target.value})} /></div>
            <div><label className="form-label">Branch Manager</label>
              <select className="form-input" value={form.manager_id} data-testid="branch-manager-select"
                onChange={e => { const emp = employees.find(x => x.user_id === e.target.value); setForm({ ...form, manager_id: e.target.value, manager_name: emp ? emp.name : '' }); }}>
                <option value="">No manager assigned</option>
                {employees.map(emp => <option key={emp.user_id} value={emp.user_id}>{emp.name} ({emp.role})</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_head_office} onChange={e => setForm({...form, is_head_office: e.target.checked})} className="rounded border-[hsl(0,0%,80%)]" />
              <span className="text-[hsl(0,0%,35%)] dark:text-[hsl(0,0%,65%)] text-[13px]">Head Office</span>
            </label>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-branch-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="save-branch-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
